/**
 * v3.15-1104 — Mark Returned from CF360 Modal (admin override)
 *
 * Per JOINT_IMPLEMENTATION_PLAN §2.2 + JOINT_OPEN_ITEMS_RESOLUTION B-1.
 *
 * Strict confirmation:
 *   1. Type the client's last name (case-insensitive match)
 *   2. One-line reason (required, max 500 chars)
 *
 * Server enforces rate-limit (≤ 5 overrides per actor per day) and role
 * gating. This modal is rendered only when the dormant banner is visible
 * AND the current user has an authorized override role (Admin/Director/
 * Manager). The banner itself hides the button for everyone else.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const MarkReturnedFromCF360Modal = ({ open, onOpenChange, client, onSuccess }) => {
  const [confirmLastName, setConfirmLastName] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmLastName('');
      setReason('');
    }
  }, [open]);

  const expectedLastName = useMemo(() => {
    const full = (client?.full_name || '').trim();
    if (!full) return '';
    const parts = full.split(/\s+/);
    return parts[parts.length - 1] || '';
  }, [client]);

  const lastNameMatches =
    expectedLastName &&
    confirmLastName.trim().toLowerCase() === expectedLastName.toLowerCase();

  const canSubmit = lastNameMatches && reason.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/clients/${client.id}/mark-returned-from-cf360`, {
        confirm_last_name: confirmLastName,
        reason: reason.trim(),
      });
      const d = res?.data;
      if (d?.success) {
        toast.success('Client marked as returned from CF360.');
        onOpenChange?.(false);
        onSuccess?.(d);
      } else {
        toast.error('Mark-returned did not complete.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err?.message;
      toast.error(`Mark-returned failed: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cf360-mark-returned-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Manually Mark Returned from CF360
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Client</div>
            <div className="font-semibold text-slate-900 mt-0.5">
              {client?.full_name || '—'}
            </div>
            {client?.cf360_sent_at && (
              <div className="text-xs text-slate-500 mt-0.5">
                Sent to CF360 at {client.cf360_sent_at.slice(0, 19).replace('T', ' ')} UTC
              </div>
            )}
          </div>

          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950 text-xs">
            This action is an <strong>admin override</strong>. The activity
            timeline records you as the actor. Hub automations resume on the
            next worker tick. Use only when CF360 has not produced the
            return event itself.
          </div>

          <div>
            <Label htmlFor="cf360-mr-last-name" className="text-xs">
              Type the client&apos;s last name to confirm
            </Label>
            <Input
              id="cf360-mr-last-name"
              data-testid="cf360-mark-returned-last-name-input"
              autoComplete="off"
              value={confirmLastName}
              onChange={(e) => setConfirmLastName(e.target.value)}
              disabled={submitting}
            />
            {confirmLastName && !lastNameMatches && (
              <div className="text-xs text-rose-700 mt-1">
                Doesn&apos;t match the client&apos;s last name.
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="cf360-mr-reason" className="text-xs">
              Reason (required)
            </Label>
            <Textarea
              id="cf360-mr-reason"
              data-testid="cf360-mark-returned-reason-input"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., CF360 confirmed completion via email; missed return-event"
              disabled={submitting}
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">
              {reason.length}/500
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="cf360-mark-returned-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="cf360-mark-returned-confirm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Confirm Mark Returned
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MarkReturnedFromCF360Modal;
